import _ from "lodash";
import React from "react";
import UIBaseComponent from "UIComponents/UIBaseComponent";
import I18nHOC from "UIComponents/I18nHOC";
import {
  Dropdown,
  DropdownMenu,
  Button,
  ChecklistDropdown,
  ButtonDisclosure,
  Tooltip,
} from "@toddle-edu/ds-web";
import PropTypes from "prop-types";
import { compose } from "lib/apolloClient";
import { connect } from "react-redux";
import { getLabelValueForDropdown } from "modules/Services";
import classNames from "classnames";
import classes from "./FieldDropdown.scss";
import UnitSelection from "UnitPlans/components/IBcomponents/UnitSelection";
import {
  getFieldValue,
  getResolvedMinimalTree,
  getUnitPlanFromAssessment,
} from "Assessments/modules/Utils";
import { getAssessmentCourseDetailsFromCache } from "Assessments/modules/AssessmentGraphqlHelpers";
import { getCurrentCurriculumProgramType } from "modules/CommonSelectors";

class FieldDropdown extends UIBaseComponent {
  constructor(props) {
    super(props);
  }

  componentDidUpdate = prevProps => {
    const {
      value,
      fieldKey,
      resourceFields,
      mode,
      updateInputField,
    } = this.props;

    const { value: prevValue } = prevProps;

    const options = this.getOptions();

    switch (fieldKey) {
      // By default we create assessment of type "pt" but if school has customized template and ...
      // removed "pt" from assessmentTypes then we need to update the value to first option which is present
      case "assessmentType": {
        if (!_.isEmpty(options)) {
          if (_.isEmpty(_.find(options, { key: value }))) {
            this.updateSingleValue({ key: _.first(options)?.key });
          }
        }
        break;
      }
      // if unit is tagged, and unit has only one level then that level should be pre-selected
      // If unit is not tagged, and class has only one level, then that level should be preselected
      case "subjectLevel": {
        const { value: initialValue } = this.getInitialSubjectLevelValue();

        const updateSubjectLevel =
          mode === "edit" &&
          _.isNull(value) &&
          _.size(initialValue) === 1 &&
          !_.isEmpty(_.find(resourceFields, { uid: "subjectLevel" }));

        if (updateSubjectLevel) {
          this.updateMultiValue(initialValue);
        }

        break;
      }
      // whenever unit changes, update the subject level accordingly
      case "unitPlan": {
        if (value !== prevValue) {
          const {
            value: initialValue,
            options: nodes,
          } = this.getInitialSubjectLevelValue();

          if (
            mode === "edit" &&
            !_.isEmpty(_.find(resourceFields, { uid: "subjectLevel" }))
          ) {
            // when switching unit if selected unit has only one level then that level should be pre-selected
            // else reset the subject level
            if (_.size(initialValue) === 1) {
              updateInputField({ subjectLevel: initialValue }, { nodes });
            } else {
              updateInputField({ subjectLevel: [] }, { nodes: [] });
            }
          }
        }
      }
    }
  };

  getInitialSubjectLevelValue = () => {
    const { resourceFields, courseId, curriculumType } = this.props;

    const unitPlan = getUnitPlanFromAssessment({
      assessmentDetails: { allFields: resourceFields },
    });

    if (unitPlan) {
      const value = getFieldValue({
        fields: unitPlan?.allFields,
        uid: "subjectLevel",
      });

      const resolvedMinimalTree = getResolvedMinimalTree({
        fields: unitPlan?.allFields,
        uid: "subjectLevel",
      });

      const options = _.map(resolvedMinimalTree?.genericTags, option => {
        return {
          ...(option || {}),
          value: _.get(option, "id"),
        };
      });

      return { value, options };
    } else {
      const courseDetails = getAssessmentCourseDetailsFromCache({
        id: courseId,
      });

      const genericTags = _.get(courseDetails, "genericTags", []);

      return {
        value: _.map(genericTags, "id"),
        options: _.map(genericTags, option => {
          return {
            ...(option || {}),
            value: _.get(option, "id"),
          };
        }),
      };
    }
  };

  renderButtonLabel = () => {
    const {
      t,
      value,
      placeholder,
      disabled,
      isUnitPlan,
      isSinglePageAndNewUnitView,
    } = this.props;
    const labelClasses = classNames({
      [classes.buttonLabel]: true,
      [classes.disabledLabel]: disabled,
      [classes.ellipsisLabel]: !_.isArray(value), // for array we show "A, B and 5 more"
    });

    const unitPlanLabelClasses = classNames({
      [classes.buttonLabelStyles]: true,
      [classes.ellipsisLabel]: !_.isArray(value),
      "text-body": true,
    });

    if (_.isEmpty(value)) {
      return (
        <div
          className={classNames({
            [classes.placeholderV2]: isSinglePageAndNewUnitView,
            "text-body": isSinglePageAndNewUnitView,
            [classes.placeholder]: !isSinglePageAndNewUnitView,
          })}
        >
          {!_.isEmpty(placeholder) ? placeholder : t("common:select")}
        </div>
      );
    } else {
      const value = this.getValue();
      const selectedValue = this.props.value;

      const showTooltip = _.isArray(selectedValue)
        ? _.size(selectedValue) > 2
        : _.size(selectedValue) > 25;

      const tooltipValue = showTooltip ? this.getTooltipValue() : null;

      return (
        <Tooltip
          tooltip={tooltipValue}
          placement={"top"}
          visible={showTooltip}
          showArrow={true}
        >
          <div className={isUnitPlan ? unitPlanLabelClasses : labelClasses}>
            {value}
          </div>
        </Tooltip>
      );
    }
  };

  updateMultiValue = params => {
    const { fieldKey } = this.props;

    const options = this.getOptions();

    const selectedOptions = _.filter(options, option => {
      return _.includes(params, option?.key);
    });

    switch (fieldKey) {
      case "pedagogicalApproachDP": {
        const updatedValues = _.map(options, option => {
          const { id, label } = option;
          if (_.includes(params, option?.key)) {
            return {
              id,
              value: label,
              isSelected: true,
            };
          } else {
            return {
              id,
              value: label,
              isSelected: false,
            };
          }
        });

        this.updateValue(updatedValues, { nodes: options });
        break;
      }
      default:
        this.updateValue(_.map(selectedOptions, "value"), { nodes: options });
    }
  };

  updateSingleValue = ({ key }) => {
    const { shouldShowArrayValues } = this.props;
    const selectedOption = _.find(this.getOptions(), { key });
    let value = _.get(selectedOption, "value", "");
    if (shouldShowArrayValues) {
      value = [value];
    }
    this.updateValue(value, {
      nodes: [selectedOption],
    });
  };

  getOptions = () => {
    const { options } = this.props;

    return _.map(options, option => {
      return {
        ...option,
        key:
          _.get(option, "key") || _.get(option, "id") || _.get(option, "value"),
      };
    });
  };

  getTooltipValue = () => {
    const { value } = this.props;

    const options = this.getOptions();

    if (_.isArray(value)) {
      return (
        <div>
          {_.map(value, selectedValue => {
            const selectedOption = _.find(options, { value: selectedValue });
            const label = selectedOption ? selectedOption.label : selectedValue;
            return <div key={selectedValue}>{label}</div>;
          })}
        </div>
      );
    } else {
      if (!_.isEmpty(options)) {
        return _.get(_.find(options, { value }), "label", "");
      } else if (_.isString(value)) {
        return value;
      } else {
        return "";
      }
    }
  };
  getValue = () => {
    const { t, value, placeholder } = this.props;

    const options = this.getOptions();

    if (_.isArray(value)) {
      return getLabelValueForDropdown({
        selectedItems: value,
        itemList: !_.isEmpty(options)
          ? options
          : _.map(value, val => {
              return {
                label: val,
                value: val,
              };
            }),
        noneSelectedText: !_.isEmpty(placeholder)
          ? placeholder
          : t("common:select"),
        showLabelsCount: 2,
        separator: t("common:and"),
        showAllText: false,
        moreLabel: "common:more_lowercase",
        t,
      });
    } else {
      if (!_.isEmpty(options)) {
        return _.get(_.find(options, { value }), "label", "");
      } else if (_.isString(value)) {
        return value;
      } else {
        return "";
      }
    }
  };

  renderButton = () => {
    const {
      uiType = "BUTTON",
      disabled = false,
      testId,
      buttonStyle,
      label,
      isFullWidth,
      isSinglePageAndNewUnitView,
      isError,
    } = this.props;

    const className = classNames({
      [classes.fullWidth]: isFullWidth,
      [classes.buttonStyles]: isSinglePageAndNewUnitView,
      [classes.errorStyles]: isError,
    });
    const buttonProps = {};
    if (isSinglePageAndNewUnitView) {
      buttonProps.dsVersion = "2.0";
      buttonProps.size = "small";
    }

    switch (uiType) {
      case "BUTTON": {
        return (
          <Button
            _legacy_variant="plain"
            size="medium"
            variant="neutral"
            type="plain"
            shouldStopPropagation={true}
            className={className}
            disabled={disabled}
            testId={`${testId}-${_.camelCase(label)}`}
            style={buttonStyle}
            {...buttonProps}
          >
            {this.renderButtonLabel()}
          </Button>
        );
      }
      case "BUTTON_DISCLOSURE": {
        return (
          <ButtonDisclosure
            _legacy_variant={"outlined-subtle"}
            variant="neutral"
            type="outlined"
            size={"medium"}
            shouldStopPropagation={true}
            className={className}
            disabled={disabled}
            testId={`${testId}-${_.camelCase(label)}`}
            style={buttonStyle}
            {...buttonProps}
          >
            {this.renderButtonLabel()}
          </ButtonDisclosure>
        );
      }
    }
  };

  renderChecklistDropdown = () => {
    const {
      value,
      disabled = false,
      overlayStyle = {},
      getPopupContainer,
      testId,
      label,
      isFullWidth,
      isSinglePageAndNewUnitView,
      dropdownContainerStyle,
    } = this.props;

    const checklistDropdownClass = classNames({
      [classes.fullWidth]: isFullWidth,
      [classes.overlayWithError]: this.state.error,
    });

    const dropdownContainerClass = classNames({
      [classes.fullWidth]: isFullWidth,
    });
    const checklistProps = {};
    if (isSinglePageAndNewUnitView) {
      checklistProps.dsVersion = "2.0";
    }
    return (
      <div className={dropdownContainerClass} style={dropdownContainerStyle}>
        <ChecklistDropdown
          {...checklistProps}
          showAllOption={true}
          options={this.getOptions()}
          value={value || []}
          onChange={this.updateMultiValue}
          className={checklistDropdownClass}
          style={overlayStyle}
          disabled={disabled}
          showSelectedOptionsOnTop={false}
          getPopupContainer={getPopupContainer}
          testId={`${testId}-${_.camelCase(label)}`}
        >
          {this.renderButton()}
        </ChecklistDropdown>
      </div>
    );
  };

  renderDropdown = () => {
    const {
      disabled,
      overlayStyle,
      getPopupContainer,
      testId,
      label,
      value,
      isFullWidth,
      showSelection,
      dropdownContainerStyle,
      isSearchEnabled = false,
      isError = false,
    } = this.props;

    const overlayClassName = classNames({
      [classes.fullWidth]: isFullWidth,
      [classes.overlayWithError]: this.state.error,
      [classes.errorStyles]: isError,
    });

    const dropdownContainerClass = classNames({
      [classes.fullWidth]: isFullWidth,
    });

    return (
      <div className={dropdownContainerClass} style={dropdownContainerStyle}>
        <Dropdown
          overlay={
            <DropdownMenu
              value={value}
              options={this.getOptions()}
              onClick={this.updateSingleValue}
              shouldStopPropagation={true}
              testId={`${testId}-${_.camelCase(label)}`}
              showSelection={showSelection}
              hasMultilineOptions={true}
              isSearchEnabled={isSearchEnabled}
            />
          }
          disabled={disabled}
          overlayClassName={overlayClassName}
          overlayStyle={overlayStyle}
          getPopupContainer={getPopupContainer}
        >
          {this.renderButton()}
        </Dropdown>
      </div>
    );
  };

  renderEditDropdownBasedOnKey = () => {
    const { multi, fieldKey, label, testId } = this.props;
    switch (fieldKey) {
      case "unitPlan":
        return (
          <UnitSelection
            {...this.props}
            label={""}
            labelComponent={null}
            testId={`${testId}-${_.camelCase(label)}`}
          />
        );
      default:
        return multi ? this.renderChecklistDropdown() : this.renderDropdown();
    }
  };

  renderViewDropdownBasedOnKey = () => {
    const {
      fieldKey,
      label,
      testId,
      isUnitPlan,
      viewContainerStyle,
    } = this.props;
    const viewContainerClass = classNames({
      [classes.viewContainer]: !isUnitPlan,
      [classes.isUnitViewContainer]: isUnitPlan,
    });

    switch (fieldKey) {
      case "unitPlan":
        return (
          <UnitSelection
            {...this.props}
            label={""}
            labelComponent={null}
            testId={`${testId}-${_.camelCase(label)}`}
          />
        );
      default:
        return (
          <div className={viewContainerClass} style={viewContainerStyle}>
            {this.renderButtonLabel()}
          </div>
        );
    }
  };

  renderEdit = () => this.renderEditDropdownBasedOnKey();
  renderView = () => this.renderViewDropdownBasedOnKey();
}

const mapStateToProps = (state, ownProps) => {
  const { mode, disabled = false, applyDefaultStyles = true } = ownProps;

  const selectedCourseId = _.get(
    state,
    "teacher.selected_class.selected_course",
    ""
  );

  const courseId = !_.isEmpty(selectedCourseId)
    ? selectedCourseId
    : ownProps?.selectedCourseId;

  const defaultStyles = {
    containerStyle: {
      flexDirection: "row",
      alignItems: "center",
    },
    labelContainerStyle: {
      width: "240px",
      margin: "0px",
    },
  };

  return {
    ...(applyDefaultStyles ? defaultStyles : {}),
    courseId,
    disabled: disabled || mode === "view",
    curriculumType: getCurrentCurriculumProgramType(state),
  };
};

FieldDropdown.defaultProps = {
  ...UIBaseComponent.defaultProps,
  options: [],
  multi: false,
  overlayStyle: {},
  buttonStyle: {},
  testId: "UIComponents-fieldDropdown",
  isFullWidth: true,
  dropdownContainerStyle: {},
  viewContainerStyle: {},
};

FieldDropdown.propTypes = {
  ...UIBaseComponent.propTypes,
  options: PropTypes.array,
  multi: PropTypes.bool,
  overlayStyle: PropTypes.object,
  buttonStyle: PropTypes.object,
  testId: PropTypes.string,
  isFullWidth: PropTypes.bool,
  dropdownContainerStyle: PropTypes.object,
  viewContainerStyle: PropTypes.object,
};

export default compose(
  I18nHOC({ resource: ["common"] }),
  connect(mapStateToProps, null)
)(FieldDropdown);
